import { CreateCounterInput, FakeInput } from 'src/api/api.generated'

//TODO remove this file once it's imported from backend core
type InvalidErrorEntityType = 'MISSING' | 'INVALID' | 'DUPLICATED' | 'NON_POSITIVE' | 'CYCLIC_RELATION' | 'CONFLICT' | 'ALREADY_EXISTS'

export type InvalidEntityErrorDetail = {
  field?: string
  errorType: InvalidErrorEntityType
  message?: string
}

export type InvalidEntityError = {
  entity: string
  errors: InvalidEntityErrorDetail[]
}

export type InvalidDocumentType = 'CASHDESK_CLOSED' | 'CUSTOMER_INVALID' | 'DOCUMENT_NOT_ALIGNED' | 'NEGATIVE_VAT' | 'COUNTER_DUPLICATED'

const DocumentErrorMessagesMap = {
  CASHDESK_CLOSED: (details?: { key: string; message: string }) => `Cash desk closed ${details ? ` ${details.key}: ${details.message}` : ''}`,
  CUSTOMER_INVALID: (details?: { key: string; message: string }) => `Invalid customer ${details ? ` ${details.key}: ${details.message}` : ''}`,
  DOCUMENT_NOT_ALIGNED: (details?: { key: string; message: string }) => `Document not aligned ${details ? ` ${details.key}: ${details.message}` : ''}`,
  NEGATIVE_VAT: (details?: { key: string; message: string }) => `Negative vat ${details ? ` ${details.key}: ${details.message}` : ''}`,
  COUNTER_DUPLICATED: (details?: { key: string; message: string }) => `Counter duplicated ${details ? ` ${details.key}: ${details.message}` : ''}`,
}

export type DocumentErrorCode = keyof typeof DocumentErrorMessagesMap
export const documentErrorCodes: DocumentErrorCode[] = Object.keys(DocumentErrorMessagesMap) as DocumentErrorCode[]

export const DocumentErrorCode: { [Property in DocumentErrorCode]: Property } = {
  CASHDESK_CLOSED: 'CASHDESK_CLOSED',
  CUSTOMER_INVALID: 'CUSTOMER_INVALID',
  DOCUMENT_NOT_ALIGNED: 'DOCUMENT_NOT_ALIGNED',
  NEGATIVE_VAT: 'NEGATIVE_VAT',
  COUNTER_DUPLICATED: 'COUNTER_DUPLICATED',
}

export type DocumentErrorMessagesMap = typeof DocumentErrorMessagesMap
export type DocumentErrorDetails<N extends DocumentErrorCode> = typeof DocumentErrorMessagesMap[N] extends (details?: any) => string ? Parameters<typeof DocumentErrorMessagesMap[N]>[0] : undefined
export type DocumentErrorDetailsMap = {
  [P in DocumentErrorCode]: DocumentErrorDetails<P>
}

const CoreErrorMessagesMap = {
  BAD_PARAMETER: (details?: { key: string; message: string }) => `Bad parameter${details ? ` ${details.key}: ${details.message}` : ''}`,
  BILL_NOT_FOUND: (details?: { reservedResourceId: string }) => `Bill not found${details ? ` for reservedResourceId ${details.reservedResourceId}` : ''}`,
  CONFIGURATION_KEY: (details?: { key: string; message: string }) => `Configuration error for key '${details?.key}': ${details?.message}`,
  COUNTER_ALREADY_EXISTS: (details?: CreateCounterInput) =>
    details
      ? `Counter ${details?.type} (tenantId: ${details?.tenantId}, propertyId:${details?.propertyId ?? 'N/A'}, subType: ${details?.subType ?? 'N/A'}) for year ${details?.year} already exists!`
      : 'Counter already exists!',
  COUNTER_NOT_FOUND: (details?: { type: string; subType?: string | null }) => `Counter of type ${details?.type ?? 'N/A'}${details?.subType ? ' (subType:s ' + details.subType + ' )' : ''} not found`,
  DAILY_AVAILABILITY_NOT_FOUND: (details?: { date: Date; resourceTypeId: string }) =>
    `DailyAvailability for resourceTypeId ${details?.resourceTypeId ?? 'N/A'} of ${details?.date ?? 'N/A'} not found.`,
  ENTITY_NOT_FOUND: (details?: { entity?: string; id: string }) => `Entity ${details?.entity ?? 'N/A'} with id ${details?.id ?? 'N/A'} not found.`,
  ENTITY_REFERENCE: (details?: { id: string }) => `Entity with id ${details?.id ?? 'N/A'} cannot be eliminated because it has relationships.`,
  EXTERNAL_SERVICE: (details?: { service: string; message: string }) => `Error calling service '${details?.service}': ${details?.message}`,
  FAKE_ERROR: (details?: FakeInput) => `Fake error. Input ${JSON.stringify(details)} is not valid, expected to be { ia: 'IA', ib: 0 }.`,
  FISCAL_GATEWAY_BAD_REQUEST: (details?: { code: number; message: string }) => `Fiscal gateway bad request. code: ${details?.code}, message: ${details?.message}`,
  GENERIC: (details?: { message: string }) => details?.message ?? 'Generic error.',
  INVALID_DOCUMENT: (details?: { type: InvalidDocumentType; message?: string }) => `Error during Document Generation: ${details?.message ? details?.message : details?.type}`,
  INVALID_ENTITY: (details?: InvalidEntityError) =>
    details ? `Validation errors in '${details?.entity}': ` + details?.errors.map((x) => x.field + (x.message ? ` - ${x.message}` : '') + ` (${x.errorType})`).join('; ') : 'Validation error',
  INVALID_OPERATION: (details?: { message: string }) => details?.message ?? 'Invalid counter error.',
  INVALID_PLACEHOLDER_FORMAT: (details?: { message: string }) => details?.message ?? 'Invalid counter format.',
  MISSING_ONEPLATFORM_CREDENTIALS: 'No OnePlatform credentials available during a OnePlatform server to server call.',
  MISSING_ONEPLATFORM_REGISTRY_COMPANY_UUID: 'No OnePlatform registry company uuid available.',
  OTA_CONFIGURATION: (details?: { entity: string; key: string; value: string }) => (details ? `${details?.entity}.${details?.key} not found with value ${details?.value}` : 'OTA Configuration Error'),
  OVERBOOKING_NOT_ALLOWED: (details?: { date: Date; resourceTypeId: string }) => `Overbooking for resourceTypeId ${details?.resourceTypeId ?? 'N/A'} not allowed`,
  RESOURCE_NOT_AVAILABLE: (details?: { date: Date; resourceId: string }) => `ResourceId ${details?.resourceId ?? 'N/A'} not available on ${details?.date ?? 'N/A'}.`,
  TSPAY_ERROR: (details?: { message: string }) => `TSPAY error: ${details?.message}`,
  CLOSED_BILL: () => '',
  HORECAHUB_INTEGRATION_NOT_REGISTERED_FOR_PRODUCT: (details?: { message: string }) => `HORECA HUB error: ${details?.message}`,
  RESOURCETYPE_INVENTORY_OVERFLOW: (details?: { message: string }) => details?.message ?? 'ResourceType inventory exceeds the licence limit',
} as const

export type CoreErrorCode = keyof typeof CoreErrorMessagesMap
export const coreErrorCodes: CoreErrorCode[] = Object.keys(CoreErrorMessagesMap) as CoreErrorCode[]

export const CoreErrorCode: { [Property in CoreErrorCode]: Property } = {
  BAD_PARAMETER: 'BAD_PARAMETER',
  BILL_NOT_FOUND: 'BILL_NOT_FOUND',
  CONFIGURATION_KEY: 'CONFIGURATION_KEY',
  COUNTER_ALREADY_EXISTS: 'COUNTER_ALREADY_EXISTS',
  COUNTER_NOT_FOUND: 'COUNTER_NOT_FOUND',
  DAILY_AVAILABILITY_NOT_FOUND: 'DAILY_AVAILABILITY_NOT_FOUND',
  ENTITY_NOT_FOUND: 'ENTITY_NOT_FOUND',
  ENTITY_REFERENCE: 'ENTITY_REFERENCE',
  EXTERNAL_SERVICE: 'EXTERNAL_SERVICE',
  FAKE_ERROR: 'FAKE_ERROR',
  FISCAL_GATEWAY_BAD_REQUEST: 'FISCAL_GATEWAY_BAD_REQUEST',
  GENERIC: 'GENERIC',
  INVALID_DOCUMENT: 'INVALID_DOCUMENT',
  INVALID_ENTITY: 'INVALID_ENTITY',
  INVALID_OPERATION: 'INVALID_OPERATION',
  INVALID_PLACEHOLDER_FORMAT: 'INVALID_PLACEHOLDER_FORMAT',
  MISSING_ONEPLATFORM_CREDENTIALS: 'MISSING_ONEPLATFORM_CREDENTIALS',
  MISSING_ONEPLATFORM_REGISTRY_COMPANY_UUID: 'MISSING_ONEPLATFORM_REGISTRY_COMPANY_UUID',
  OTA_CONFIGURATION: 'OTA_CONFIGURATION',
  OVERBOOKING_NOT_ALLOWED: 'OVERBOOKING_NOT_ALLOWED',
  RESOURCE_NOT_AVAILABLE: 'RESOURCE_NOT_AVAILABLE',
  TSPAY_ERROR: 'TSPAY_ERROR',
  CLOSED_BILL: 'CLOSED_BILL',
  HORECAHUB_INTEGRATION_NOT_REGISTERED_FOR_PRODUCT: 'HORECAHUB_INTEGRATION_NOT_REGISTERED_FOR_PRODUCT',
  RESOURCETYPE_INVENTORY_OVERFLOW: 'RESOURCETYPE_INVENTORY_OVERFLOW',
}

export type CoreErrorMessagesMap = typeof CoreErrorMessagesMap
export type CoreErrorDetails<N extends CoreErrorCode> = typeof CoreErrorMessagesMap[N] extends (details?: any) => string ? Parameters<typeof CoreErrorMessagesMap[N]>[0] : undefined
export type CoreErrorDetailsMap = {
  [P in CoreErrorCode]: CoreErrorDetails<P>
}
